// Planilha real (jspreadsheet) do controle de budget — comportamento estilo
// Google Sheets/Excel: seleção por clique e arrasto, copiar/colar, menu de
// contexto, linha de título e linha em branco no final para novas máquinas.
//
// A planilha é PIVOTADA: cada máquina ocupa UMA linha e cada rubrica (M.O
// instalação, projeto mecânico, projeto elétrico...) vira um grupo de colunas
// com o seu próprio budget e as suas etapas. O saldo é recalculado em JS (sem
// fórmulas, evitando #ERROR com célula vazia).
//
// Rubrica DESVINCULADA (ex.: "despesa" = Hospedagem, Frete, Refeição) não entra
// no pivô: as linhas dela vão num bloco no FINAL da planilha e as células que
// cruzam bloco e máquina ficam travadas, para um lado nunca somar no outro.

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Undo2, Redo2, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import jspreadsheet from 'jspreadsheet-ce';
import 'jspreadsheet-ce/dist/jspreadsheet.css';
import 'jsuites/dist/jsuites.css';
import './budget-sheet.css';
import { EtapaCol, GrupoRubrica, LinhaPivot, linhaVazia, moeda, parseValor, valorEtapa } from './budget-types';

export interface BudgetSheetHandle {
  /** Ids das máquinas nas linhas selecionadas (clique e arrasto na planilha) */
  getMaquinasSelecionadas: () => string[];
}

interface BudgetSheetProps {
  obraId: string;
  obraNome: string;
  linhas: LinhaPivot[];
  grupos: GrupoRubrica[];
  /** Incrementar para forçar a reconstrução da planilha com os dados atuais */
  versao: number;
  /** `rubricaId` só vem nas linhas de bloco desvinculado, que renomeiam sozinhas */
  onRenomearMaquina: (de: string, para: string, rubricaId?: string) => void;
  onEditarBudget: (maquinaId: string, valor: number) => void;
  onEditarEtapa: (maquinaId: string, etapa: EtapaCol, valor: number) => void;
  /** Cria a linha da máquina dentro de uma rubrica e devolve o id */
  onCriarLinha: (nome: string, rubricaId: string, budget: number) => Promise<string | null>;
  onRemoverMaquinas: (ids: string[]) => void;
  /** Recarrega tudo do servidor (usado quando o undo/redo mexe na estrutura de linhas) */
  onResync: () => void;
  /** Nome de TODAS as rubricas da obra — o aviso de exclusão precisa citar as que estão fora da tela */
  nomesDasRubricas: Record<string, string>;
}

/** Colunas de um grupo: budget + uma coluna por etapa da rubrica */
const largura = (g: GrupoRubrica) => 1 + g.etapas.length;

/**
 * A célula (linha × grupo) é editável? Linha de um bloco desvinculado só aceita a
 * rubrica dele; linha de máquina não aceita rubrica desvinculada nenhuma. É o que
 * mantém a "despesa" independente do resto das rubricas.
 */
const celulaDoBloco = (bloco: string | undefined, grupo: GrupoRubrica) =>
  bloco ? grupo.id === bloco : grupo.vinculada;

const formatoNumero = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Cabeçalho curto: o tipo vira RE/PR para a coluna não esticar */
const tituloEtapa = (e: EtapaCol) =>
  `${e.nome ?? 'Sem etapa'} · ${e.tipo === 'previsto' ? 'PR' : 'RE'}`;

/**
 * Largura que cabe o maior valor da coluna, com o título quebrando em até duas
 * linhas. Sem isso a coluna herda o tamanho do texto do cabeçalho e, com várias
 * rubricas lado a lado, a planilha estoura a tela.
 */
function larguraDaColuna(titulo: string, conteudos: string[], min: number, max: number) {
  const maiorConteudo = conteudos.reduce((m, v) => Math.max(m, v.length), 0);
  const maiorPalavra = titulo.split(/\s+/).reduce((m, p) => Math.max(m, p.length), 0);
  const tituloEmDuasLinhas = Math.max(maiorPalavra, Math.ceil(titulo.length / 2));
  return Math.round(Math.min(max, Math.max(
    min,
    maiorConteudo * 7.6 + 26,
    tituloEmDuasLinhas * 6.6 + 22
  )));
}

export const BudgetSheet = forwardRef<BudgetSheetHandle, BudgetSheetProps>(function BudgetSheet(props, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<any>(null);
  // Linhas do pivô por índice de linha da planilha (undefined = linha ainda vazia)
  const rowsRef = useRef<Array<LinhaPivot | undefined>>([]);
  // Última seleção feita na grade (o jexcel limpa a seleção ao clicar fora,
  // então guardamos aqui para o "Selecionar na planilha" funcionar)
  const ultimaSelecaoRef = useRef<string[]>([]);
  // Linhas marcadas com Ctrl/⌘ + clique (o jspreadsheet só sabe faixa contínua)
  const marcadasRef = useRef<Set<number>>(new Set());
  // Linhas de budget criadas aqui desde a última montagem. Apagar o nome só
  // remove do banco se a linha nasceu agora (desfazer a criação); numa linha que
  // já existia isso apagaria máquina de verdade sem aviso nenhum.
  const criadasAquiRef = useRef<Set<string>>(new Set());
  const programaticoRef = useRef(false);
  const propsRef = useRef(props);
  propsRef.current = props;

  const layoutKey = props.grupos
    .map((g) => `${g.id}[${g.etapas.map((e) => `${e.key}:${e.tipo}`).join(',')}]`)
    .join('|');

  /** Índice da 1ª coluna de cada grupo (col 0 = nome da máquina) */
  const inicioDoGrupo = (grupos: GrupoRubrica[], indice: number) =>
    1 + grupos.slice(0, indice).reduce((s, g) => s + largura(g), 0);

  /** Descobre a que grupo/etapa pertence uma coluna da grade */
  const localizarColuna = (grupos: GrupoRubrica[], x: number) => {
    let inicio = 1;
    for (const grupo of grupos) {
      const fim = inicio + largura(grupo);
      if (x >= inicio && x < fim) {
        const offset = x - inicio;
        return { grupo, etapa: offset === 0 ? null : grupo.etapas[offset - 1] };
      }
      inicio = fim;
    }
    return null;
  };

  const valorDaCelula = (linha: LinhaPivot, grupo: GrupoRubrica, etapa: EtapaCol | null) => {
    if (!celulaDoBloco(linha.grupoExclusivo, grupo)) return '';
    const m = linha.porRubrica[grupo.id];
    if (!m) return '';
    if (!etapa) return m.budget || '';
    return valorEtapa(m, etapa) || '';
  };

  // Exporta a obra em Excel (.xlsx) no mesmo layout pivotado da planilha
  const exportarExcel = () => {
    const { linhas, grupos, obraNome } = propsRef.current;
    const grupoHeader = [
      '',
      ...grupos.flatMap((g) => [g.nome, ...g.etapas.map(() => '')]),
      'TOTAL', '', '',
    ];
    const cabecalho = [
      'Máquina',
      ...grupos.flatMap((g) => [
        'Budget (R$)',
        ...g.etapas.map((e) => `${e.nome ?? 'Sem etapa'} — ${e.tipo === 'previsto' ? 'previsto' : 'realizado'}`),
      ]),
      'Budget total', 'Gasto', 'Saldo',
    ];
    // Célula fora do bloco da linha sai vazia (e não zero), como na tela
    const budgetDaLinha = (l: LinhaPivot, g: GrupoRubrica) =>
      celulaDoBloco(l.grupoExclusivo, g) ? l.porRubrica[g.id]?.budget ?? 0 : '';
    const etapaDaLinha = (l: LinhaPivot, g: GrupoRubrica, e: EtapaCol) => {
      const m = celulaDoBloco(l.grupoExclusivo, g) ? l.porRubrica[g.id] : undefined;
      return m ? valorEtapa(m, e) || 0 : '';
    };
    const corpo = linhas.map((l) => [
      l.nome,
      ...grupos.flatMap((g) => [budgetDaLinha(l, g), ...g.etapas.map((e) => etapaDaLinha(l, g, e))]),
      l.budgetTotal, l.gastoRealizado, l.saldo,
    ]);
    const soma = (fn: (l: LinhaPivot) => number) => linhas.reduce((s, l) => s + fn(l), 0);
    const total = [
      'TOTAL',
      ...grupos.flatMap((g) => [
        soma((l) => (celulaDoBloco(l.grupoExclusivo, g) ? l.porRubrica[g.id]?.budget ?? 0 : 0)),
        ...g.etapas.map((e) => soma((l) => {
          const m = celulaDoBloco(l.grupoExclusivo, g) ? l.porRubrica[g.id] : undefined;
          return m ? valorEtapa(m, e) : 0;
        })),
      ]),
      soma((l) => l.budgetTotal), soma((l) => l.gastoRealizado), soma((l) => l.saldo),
    ];
    const ws = XLSX.utils.aoa_to_sheet([grupoHeader, cabecalho, ...corpo, total]);
    // Mescla o título de cada rubrica sobre as colunas do grupo
    ws['!merges'] = grupos.map((g, i) => {
      const c = inicioDoGrupo(grupos, i);
      return { s: { r: 0, c }, e: { r: 0, c: c + largura(g) - 1 } };
    });
    ws['!cols'] = [{ wch: 42 }, ...cabecalho.slice(1).map((t) => ({ wch: Math.max(16, t.length + 2) }))];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, (obraNome || 'Budget').substring(0, 31));
    XLSX.writeFile(wb, `budget-${(obraNome || 'obra').replace(/[\\/:*?"<>|]/g, '-')}.xlsx`);
  };

  /** Ids de budget das linhas de índice `ys` (uma linha do pivô = várias rubricas) */
  const idsDasLinhas = (ys: number[]) =>
    ys.flatMap((y) => {
      const linha = rowsRef.current[y];
      return linha ? Object.values(linha.porRubrica).filter(Boolean).map((m) => m!.id) : [];
    });

  /**
   * A que bloco desvinculado pertence a linha `y` da grade. Linha ainda sem dado
   * — a em branco do fim da planilha ou uma inserida pelo botão direito — herda
   * o bloco da linha de cima: é o que faz a linha em branco embaixo da "despesa"
   * criar despesa, e a que fica embaixo das máquinas criar máquina.
   */
  const blocoDaLinha = (y: number): string | undefined => {
    const linha = rowsRef.current[y];
    return linha ? linha.grupoExclusivo : rowsRef.current[y - 1]?.grupoExclusivo;
  };

  /** Índices das linhas marcadas na grade (arrasto do jexcel + cliques com Ctrl) */
  const linhasSelecionadas = (): number[] => {
    const inst = instanceRef.current;
    try {
      return ((inst?.getSelectedRows(true) as unknown[]) || []).map(Number);
    } catch {
      return [];
    }
  };

  useImperativeHandle(ref, () => ({
    getMaquinasSelecionadas: () => {
      const ids = idsDasLinhas(linhasSelecionadas());
      return ids.length > 0 ? ids : ultimaSelecaoRef.current;
    },
  }));

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const { linhas, grupos } = propsRef.current;

    rowsRef.current = [...linhas];
    ultimaSelecaoRef.current = [];
    marcadasRef.current.clear();
    criadasAquiRef.current.clear();

    const colBudgetTotal = () => 1 + propsRef.current.grupos.reduce((s, g) => s + largura(g), 0);

    // Recalcula "Budget total / Gasto / Saldo" da linha a partir da grade.
    // ignoreHistory evita que essa escrita entre no histórico do Desfazer/Refazer.
    const recalcularTotais = (y: number) => {
      const inst = instanceRef.current;
      const atual = propsRef.current;
      if (!inst) return;
      const base = colBudgetTotal();
      const bloco = blocoDaLinha(y);
      let budget = 0;
      let realizado = 0;
      atual.grupos.forEach((g, i) => {
        if (!celulaDoBloco(bloco, g)) return;
        const inicio = inicioDoGrupo(atual.grupos, i);
        budget += parseValor(String(inst.getValueFromCoords(inicio, y) ?? '')) ?? 0;
        g.etapas.forEach((e, j) => {
          if (e.tipo !== 'previsto') {
            realizado += parseValor(String(inst.getValueFromCoords(inicio + 1 + j, y) ?? '')) ?? 0;
          }
        });
      });
      programaticoRef.current = true;
      try {
        (inst as any).ignoreHistory = true;
        inst.setValueFromCoords(base, y, budget, true);
        inst.setValueFromCoords(base + 1, y, realizado, true);
        inst.setValueFromCoords(base + 2, y, budget - realizado, true);
      } finally {
        (inst as any).ignoreHistory = false;
        programaticoRef.current = false;
      }
    };

    const data = linhas.map((l) => [
      l.nome,
      ...grupos.flatMap((g) => [
        valorDaCelula(l, g, null),
        ...g.etapas.map((e) => valorDaCelula(l, g, e)),
      ]),
      l.budgetTotal,
      l.gastoRealizado,
      l.saldo,
    ]);

    // Texto que cada coluna precisa comportar, para dimensionar pelo conteúdo
    const textoDaColuna = (indice: number) =>
      data.map((linha) => {
        const v = linha[indice];
        return typeof v === 'number' ? formatoNumero.format(v) : String(v ?? '');
      });
    const numerico = { type: 'numeric', mask: '#.##0,00', decimal: ',', align: 'right' };
    // Valores em reais raramente passam de "1.234.567,89"; o teto evita que um
    // outlier estique a coluna, e a barra de rolagem cobre o resto.
    const colunaNumerica = (titulo: string, indice: number) => ({
      ...numerico,
      title: titulo,
      width: larguraDaColuna(titulo, textoDaColuna(indice), 88, 150),
    });

    /** Garante que a máquina existe dentro da rubrica antes de gravar o valor */
    const comLinhaDaRubrica = async (
      y: number,
      grupo: GrupoRubrica,
      acao: (maquinaId: string) => void
    ) => {
      const linha = rowsRef.current[y];
      if (!linha) return;
      const existente = linha.porRubrica[grupo.id];
      if (existente) {
        acao(existente.id);
        return;
      }
      const id = await propsRef.current.onCriarLinha(linha.nome, grupo.id, 0);
      if (!id) return;
      criadasAquiRef.current.add(id);
      linha.porRubrica[grupo.id] = {
        id,
        nome: linha.nome,
        rubricaId: grupo.id,
        budget: 0,
        gastos: [],
        gastoRealizado: 0,
        gastoPrevisto: 0,
        saldo: 0,
      };
      acao(id);
    };

    /**
     * Em que grupo nasce a linha nova: dentro de um bloco desvinculado ela é
     * item daquela rubrica; fora dele, máquina da primeira rubrica vinculada.
     */
    const grupoDaLinhaNova = (y: number) => {
      const atual = propsRef.current;
      const bloco = blocoDaLinha(y);
      if (bloco) return atual.grupos.find((g) => g.id === bloco);
      return atual.grupos.find((g) => g.vinculada) ?? atual.grupos[0];
    };

    const tratarMudanca = (x: number, y: number, valorNovo: unknown) => {
      if (programaticoRef.current) return;
      const atual = propsRef.current;
      if (x < 0 || x >= colBudgetTotal()) return;
      const inst = instanceRef.current;

      // Linha nova (linha em branco do final, slot de bloco ou linha inserida):
      // cria a linha de budget assim que o nome é digitado
      if (linhaVazia(rowsRef.current[y])) {
        if (x !== 0) return;
        const nome = String(valorNovo ?? '').trim();
        if (!nome) return;
        const primeiro = grupoDaLinhaNova(y);
        if (!primeiro) return;
        const indicePrimeiro = atual.grupos.indexOf(primeiro);
        const inicio = inicioDoGrupo(atual.grupos, indicePrimeiro);
        const budget = parseValor(String(inst?.getValueFromCoords(inicio, y) ?? '')) ?? 0;
        atual.onCriarLinha(nome, primeiro.id, budget).then((id) => {
          if (!id) return;
          criadasAquiRef.current.add(id);
          while (rowsRef.current.length <= y) rowsRef.current.push(undefined);
          const maquina = {
            id, nome, rubricaId: primeiro.id, budget,
            gastos: [], gastoRealizado: 0, gastoPrevisto: 0, saldo: budget,
          };
          rowsRef.current[y] = {
            nome,
            porRubrica: { [primeiro.id]: maquina },
            ...(primeiro.vinculada ? {} : { grupoExclusivo: primeiro.id }),
            budgetTotal: budget,
            gastoRealizado: 0,
            gastoPrevisto: 0,
            saldo: budget,
          };
          // Salva o que já tinha sido digitado no resto da linha
          atual.grupos.forEach((g, i) => {
            if (!celulaDoBloco(rowsRef.current[y]?.grupoExclusivo, g)) return;
            const iniG = inicioDoGrupo(atual.grupos, i);
            g.etapas.forEach((e, j) => {
              const v = parseValor(String(inst?.getValueFromCoords(iniG + 1 + j, y) ?? ''));
              if (v && v > 0) comLinhaDaRubrica(y, g, (mid) => propsRef.current.onEditarEtapa(mid, e, v));
            });
            if (g.id !== primeiro.id) {
              const v = parseValor(String(inst?.getValueFromCoords(iniG, y) ?? ''));
              if (v && v > 0) comLinhaDaRubrica(y, g, (mid) => propsRef.current.onEditarBudget(mid, v));
            }
          });
          recalcularTotais(y);
        });
        return;
      }

      const linha = rowsRef.current[y]!;

      if (x === 0) {
        const nome = String(valorNovo ?? '').trim();
        if (nome && nome !== linha.nome) {
          atual.onRenomearMaquina(linha.nome, nome, linha.grupoExclusivo);
          linha.nome = nome;
          return;
        }
        // Nome apagado. Só desfaz a criação de linha que nasceu nesta sessão e
        // ainda está vazia — em linha que já existia, apagar o nome NÃO apaga
        // nada: uma seleção da coluna Máquina + Delete varreria a obra inteira
        // sem confirmação. Aí o nome volta e a exclusão fica só no botão direito.
        if (!nome) {
          const ids = Object.values(linha.porRubrica).filter(Boolean).map((m) => m!.id);
          const nascidaAqui = ids.length > 0 && ids.every((id) => criadasAquiRef.current.has(id));
          const temValor = atual.grupos.some((g, i) => {
            const inicio = inicioDoGrupo(atual.grupos, i);
            const b = parseValor(String(inst?.getValueFromCoords(inicio, y) ?? '')) ?? 0;
            const etapas = g.etapas.some((_e, j) =>
              (parseValor(String(inst?.getValueFromCoords(inicio + 1 + j, y) ?? '')) ?? 0) > 0);
            return b > 0 || etapas;
          });
          if (nascidaAqui && !temValor) {
            rowsRef.current[y] = undefined;
            ids.forEach((id) => criadasAquiRef.current.delete(id));
            atual.onRemoverMaquinas(ids);
            return;
          }
          programaticoRef.current = true;
          try {
            (inst as any).ignoreHistory = true;
            inst?.setValueFromCoords(0, y, linha.nome, true);
          } finally {
            (inst as any).ignoreHistory = false;
            programaticoRef.current = false;
          }
        }
        return;
      }

      const alvo = localizarColuna(atual.grupos, x);
      if (!alvo) return;
      // Cruzamento bloco × máquina: a célula é travada, mas colar em massa ainda
      // chega aqui — apaga o que caiu nela em vez de lançar na rubrica errada.
      if (!celulaDoBloco(linha.grupoExclusivo, alvo.grupo)) {
        if (String(valorNovo ?? '') !== '') {
          programaticoRef.current = true;
          try {
            (inst as any).ignoreHistory = true;
            inst?.setValueFromCoords(x, y, '', true);
          } finally {
            (inst as any).ignoreHistory = false;
            programaticoRef.current = false;
          }
        }
        return;
      }
      const v = parseValor(String(valorNovo ?? ''));
      if (v === null) return;

      if (!alvo.etapa) {
        comLinhaDaRubrica(y, alvo.grupo, (mid) => atual.onEditarBudget(mid, v));
      } else {
        const etapa = alvo.etapa;
        comLinhaDaRubrica(y, alvo.grupo, (mid) => atual.onEditarEtapa(mid, etapa, v));
      }
      recalcularTotais(y);
    };

    // Após um undo/redo, sincroniza o servidor com o que a grade mostra agora.
    const sincronizarHistorico = (historyRecord: any, direcao: 'undo' | 'redo') => {
      const inst = instanceRef.current;
      if (!inst || !historyRecord) return;
      const acao = historyRecord.action;

      // Inserir/excluir linha mexe na estrutura toda (N rubricas por linha):
      // recarregar do servidor é mais seguro do que remontar aqui.
      if (acao === 'deleteRow' || acao === 'insertRow') {
        const numOfRows = Number(historyRecord.numOfRows) || 1;
        const base = Number(historyRecord.rowNumber) || 0;
        const pos = acao === 'insertRow' && !historyRecord.insertBefore ? base + 1 : base;
        const linhasVoltaram = (acao === 'deleteRow') === (direcao === 'undo');
        if (!linhasVoltaram) {
          const ids = rowsRef.current
            .slice(pos, pos + numOfRows)
            .flatMap((l) => (l ? Object.values(l.porRubrica).filter(Boolean).map((m) => m!.id) : []));
          rowsRef.current.splice(pos, numOfRows);
          if (ids.length > 0) propsRef.current.onRemoverMaquinas(ids);
          return;
        }
        propsRef.current.onResync();
        return;
      }

      // setValue (e demais): salva o valor ATUAL das células afetadas
      const registros: Array<{ x: string | number; y: string | number }> = historyRecord.records || [];
      registros.forEach((r) => {
        const x = Number(r.x);
        const y = Number(r.y);
        tratarMudanca(x, y, inst.getValueFromCoords(x, y));
      });
    };

    // ===== Seleção de linhas soltas (Ctrl/⌘ + clique) =====
    // O jspreadsheet só seleciona um retângulo: com Ctrl no cabeçalho da linha
    // ele ESTENDE a faixa, igual ao Shift, então não dá para pular linha. Aqui a
    // marcação é feita na mão, usando a mesma classe "selected" que o
    // getSelectedRows lê — o resto da tela continua enxergando a seleção normal.
    const marcadas = marcadasRef.current;
    const linhasDaGrade = (): HTMLTableRowElement[] => instanceRef.current?.rows ?? [];

    const pintarMarcadas = () => {
      linhasDaGrade().forEach((tr, y) => {
        if (!tr) return;
        const marcada = marcadas.has(y);
        tr.classList.toggle('budget-marcada', marcada);
        tr.classList.toggle('selected', marcada);
      });
    };

    const limparMarcadas = () => {
      if (marcadas.size === 0) return;
      marcadas.clear();
      linhasDaGrade().forEach((tr) => tr?.classList.remove('budget-marcada'));
    };

    const totalColunas = 1 + grupos.reduce((s, g) => s + largura(g), 0);
    const colunas = [
      {
        type: 'text',
        title: 'Máquina',
        align: 'left',
        width: larguraDaColuna('Máquina', textoDaColuna(0), 160, 300),
      },
      ...grupos.flatMap((g, i) => {
        const inicio = inicioDoGrupo(grupos, i);
        return [
          colunaNumerica('Budget (R$)', inicio),
          ...g.etapas.map((e, j) => colunaNumerica(tituloEtapa(e), inicio + 1 + j)),
        ];
      }),
      { ...colunaNumerica('Budget total', totalColunas), readOnly: true },
      { ...colunaNumerica('Gasto', totalColunas + 1), readOnly: true },
      { ...colunaNumerica('Saldo', totalColunas + 2), readOnly: true },
    ];

    // Cabeçalho de 1º nível: o nome da rubrica sobre as colunas do seu grupo
    const nestedHeaders = [[
      { title: '', colspan: 1 },
      ...grupos.map((g) => ({
        title: g.vinculada ? g.nome : `${g.nome} · desvinculada`,
        colspan: largura(g),
      })),
      { title: 'Consolidado', colspan: 3 },
    ]];

    // Coluna da grade → grupo, resolvido uma vez só (o updateTable roda por célula)
    const grupoDaColuna: Array<GrupoRubrica | undefined> = [];
    grupos.forEach((g, i) => {
      const inicio = inicioDoGrupo(grupos, i);
      for (let c = inicio; c < inicio + largura(g); c++) grupoDaColuna[c] = g;
    });


    const opcoes: any = {
      data: data.length > 0 ? data : [colunas.map(() => '')],
      columns: colunas,
      nestedHeaders,
      minSpareRows: 1,
      allowInsertColumn: false,
      allowManualInsertColumn: false,
      allowDeleteColumn: false,
      allowRenameColumn: false,
      columnSorting: false,
      columnDrag: false,
      rowDrag: false,
      about: false,
      tableOverflow: true,
      tableWidth: '100%',
      tableHeight: '62vh',
      freezeColumns: 1,
      text: {
        insertANewRowBefore: 'Inserir linha acima',
        insertANewRowAfter: 'Inserir linha abaixo',
        deleteSelectedRows: 'Excluir linha(s) selecionada(s)',
        copy: 'Copiar',
        paste: 'Colar',
        saveAs: 'Salvar como',
        areYouSureToDeleteTheSelectedRows: 'Tem certeza que deseja excluir as linhas selecionadas?',
      },
      // Pinta o bloco desvinculado e trava o cruzamento com as máquinas. O jexcel
      // trata como somente-leitura toda célula com a classe "readonly".
      updateTable: (_el: unknown, cell: HTMLTableCellElement, x: number, y: number) => {
        const bloco = blocoDaLinha(y);
        cell.classList.toggle('budget-bloco', !!bloco);
        // 1ª linha do bloco leva o traço que o separa das máquinas
        cell.classList.toggle('budget-bloco-inicio', !!bloco && bloco !== blocoDaLinha(y - 1));
        const grupo = grupoDaColuna[x];
        if (!grupo) return;
        const travada = !celulaDoBloco(bloco, grupo);
        cell.classList.toggle('readonly', travada);
        cell.classList.toggle('budget-travada', travada);
      },
      oninsertrow: (_el: unknown, rowIndex: number, numOfRows: number, _cells: unknown, insertBefore: boolean) => {
        // Durante a montagem o jexcel já insere a linha em branco do final, e aí
        // o rowsRef ainda nem existe — a instância só é guardada depois.
        if (!instanceRef.current) return;
        // As linhas inseridas nascem vazias: sem isso o rowsRef sai do lugar e a
        // edição passa a gravar na máquina de baixo.
        const pos = insertBefore ? rowIndex : rowIndex + 1;
        rowsRef.current.splice(pos, 0, ...Array(numOfRows).fill(undefined));
        limparMarcadas();
      },
      onafterchanges: (_el: unknown, changes: Array<{ x: string; y: string; newValue: unknown }>) => {
        (changes || []).forEach((ch) => tratarMudanca(Number(ch.x), Number(ch.y), ch.newValue));
      },
      onundo: (_el: unknown, historyRecord: any) => sincronizarHistorico(historyRecord, 'undo'),
      onredo: (_el: unknown, historyRecord: any) => sincronizarHistorico(historyRecord, 'redo'),
      onselection: (_el: unknown, _x1: number, y1: number, _x2: number, y2: number) => {
        const ini = Math.min(Number(y1), Number(y2));
        const fim = Math.max(Number(y1), Number(y2));
        const faixa = [];
        for (let y = ini; y <= fim; y++) faixa.push(y);
        const ids = idsDasLinhas(faixa);
        if (ids.length > 0) ultimaSelecaoRef.current = ids;
      },
      // Excluir linha apaga a máquina em TODAS as rubricas, inclusive nas que não
      // estão na tela — 75 linhas selecionadas viraram 225 linhas de budget uma
      // vez. O aviso lista rubrica por rubrica e cobra confirmação em cima.
      onbeforedeleterow: (_el: unknown, rowIndex: number, numOfRows: number) => {
        const alvo = rowsRef.current.slice(rowIndex, rowIndex + numOfRows).filter((l) => !linhaVazia(l));
        if (alvo.length === 0) return true;

        const naTela = new Set(propsRef.current.grupos.map((g) => g.id));
        const nomes = propsRef.current.nomesDasRubricas;
        const porRubrica = new Map<string, { linhas: number; budget: number; gastos: number }>();
        alvo.forEach((l) => Object.entries(l!.porRubrica).forEach(([id, m]) => {
          if (!m) return;
          const t = porRubrica.get(id) ?? { linhas: 0, budget: 0, gastos: 0 };
          t.linhas += 1;
          t.budget += m.budget;
          t.gastos += m.gastoRealizado + m.gastoPrevisto;
          porRubrica.set(id, t);
        }));

        const totalLinhas = [...porRubrica.values()].reduce((s, t) => s + t.linhas, 0);
        const totalBudget = [...porRubrica.values()].reduce((s, t) => s + t.budget, 0);
        const totalGastos = [...porRubrica.values()].reduce((s, t) => s + t.gastos, 0);
        const detalhe = [...porRubrica.entries()]
          .map(([id, t]) => `  • ${nomes[id] ?? 'Sem rubrica'} — ${t.linhas} linha(s), ${moeda(t.budget)}`
            + `${t.gastos > 0 ? ` e ${moeda(t.gastos)} em gastos` : ''}`
            + `${naTela.has(id) ? '' : '   ← FORA DA TELA'}`)
          .join('\n');
        const foraDaTela = [...porRubrica.keys()].filter((id) => !naTela.has(id));

        const aviso = `Excluir ${alvo.length} linha(s) da planilha?\n\n`
          + `Isso apaga ${totalLinhas} linha(s) de budget — ${moeda(totalBudget)} de budget`
          + `${totalGastos > 0 ? ` e ${moeda(totalGastos)} em gastos` : ''}:\n\n${detalhe}\n\n`
          + `${foraDaTela.length > 0 ? 'As rubricas marcadas NÃO estão na tela agora.\n' : ''}`
          + 'Não dá para desfazer.';
        if (!confirm(aviso)) return false;

        // Acima de um punhado de linhas, confirmar de novo digitando a quantidade
        if (alvo.length > 3) {
          const digitado = prompt(`Confirme: digite ${alvo.length} para apagar essas linhas.`, '');
          if (digitado === null) return false;
          if (digitado.trim() !== String(alvo.length)) {
            alert('Número diferente — nada foi apagado.');
            return false;
          }
        }
        return true;
      },
      ondeleterow: (_el: unknown, rowIndex: number, numOfRows: number) => {
        const ids = rowsRef.current
          .slice(rowIndex, rowIndex + numOfRows)
          .flatMap((l) => (l ? Object.values(l.porRubrica).filter(Boolean).map((m) => m!.id) : []));
        rowsRef.current.splice(rowIndex, numOfRows);
        limparMarcadas();
        if (ids.length > 0) propsRef.current.onRemoverMaquinas(ids);
      },
    };

    const instance = jspreadsheet(el, opcoes);
    instanceRef.current = instance;

    const cliqueNaGrade = (evento: MouseEvent) => {
      if (evento.button !== 0) return;
      if (!evento.ctrlKey && !evento.metaKey) {
        limparMarcadas();
        return;
      }
      const celula = (evento.target as HTMLElement)?.closest?.('td');
      const dataY = celula?.getAttribute('data-y');
      if (dataY === null || dataY === undefined || celula?.closest('thead')) return;
      const y = Number(dataY);
      if (!Number.isInteger(y)) return;
      // O jexcel usaria este clique para esticar a faixa até a linha; o Ctrl aqui
      // é só marcar/desmarcar, então o evento para antes de chegar nele.
      evento.preventDefault();
      evento.stopPropagation();
      // A 1ª marcação parte da seleção que já estava feita (Ctrl SOMA à seleção)
      if (marcadas.size === 0) linhasSelecionadas().forEach((n) => marcadas.add(n));
      if (marcadas.has(y)) marcadas.delete(y);
      else marcadas.add(y);
      pintarMarcadas();
      const ids = idsDasLinhas([...marcadas]);
      if (ids.length > 0) ultimaSelecaoRef.current = ids;
    };

    el.addEventListener('mousedown', cliqueNaGrade, true);

    return () => {
      el.removeEventListener('mousedown', cliqueNaGrade, true);
      marcadas.clear();
      try {
        jspreadsheet.destroy(el as any);
      } catch {
        // instância já destruída
      }
      instanceRef.current = null;
    };
    // Reconstrói apenas em troca de obra/colunas ou quando o pai pede (versão)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.obraId, layoutKey, props.versao]);

  return (
    <div className="bg-white rounded-lg shadow p-2">
      {/* Barra de ferramentas da planilha */}
      <div className="flex items-center gap-1 px-1 pb-2 border-b border-schmersal-gray-light mb-1">
        <button
          type="button"
          onClick={() => instanceRef.current?.undo()}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-schmersal-gray-dark rounded hover:bg-schmersal-gray-light"
          title="Desfazer a última digitação nesta tela (Ctrl+Z). Para voltar a planilha inteira — exclusões, lotes — use &quot;Voltar atrás&quot; na barra de cima."
        >
          <Undo2 className="h-4 w-4" /> Desfazer
        </button>
        <button
          type="button"
          onClick={() => instanceRef.current?.redo()}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-schmersal-gray-dark rounded hover:bg-schmersal-gray-light"
          title="Refazer (Ctrl+Y)"
        >
          <Redo2 className="h-4 w-4" /> Refazer
        </button>
        <span className="w-px h-5 bg-schmersal-gray-light mx-1" />
        <button
          type="button"
          onClick={exportarExcel}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-schmersal-gray-dark rounded hover:bg-schmersal-gray-light"
          title="Baixar planilha em Excel (.xlsx)"
        >
          <Download className="h-4 w-4" /> Exportar Excel
        </button>
        <span className="ml-auto text-[11px] text-schmersal-gray-medium hidden sm:block">
          Ctrl+clique marca linhas soltas · Ctrl+C / Ctrl+V colam do Excel · botão direito insere/exclui linhas
        </span>
      </div>
      <div ref={containerRef} className="overflow-x-auto" />
    </div>
  );
});
